import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CalculatorCard from "../../components/CalculatorCard";
import { Helmet } from "react-helmet-async";

export default function EmiCalculator() {
  const [loan, setLoan] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [emiData, setEmiData] = useState([]);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loanParam = params.get("loan");
    if (loanParam) setLoan(loanParam);
  }, [location.search]);

  const calculateEMI = () => {
    const P = Number(loan);
    const R = Number(rate) / 12 / 100;
    const N = Number(years) * 12;

    if (!P || !R || !N) return;

    const options = [];

    for (let i = 12; i <= N; i += 12) {
      const emi =
        (P * R * Math.pow(1 + R, i)) /
        (Math.pow(1 + R, i) - 1);

      const totalPayment = emi * i;
      const interest = totalPayment - P;

      options.push({
        months: i,
        emi: Math.round(emi),
        interest: Math.round(interest),
        total: Math.round(totalPayment),
      });
    }

    setEmiData(options);
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Check my calculation result: ${emiData.map(item => `${item.months} months: ₹${item.emi}`).join(', ')} | MotoHub India https://motohubindia.com`);
    const url = window.innerWidth < 768 
      ? `whatsapp://send?text=${text}` 
      : `https://web.whatsapp.com/send?text=${text}`;
    window.open(url, '_blank');
  };

  const downloadPDF = () => {
    const el = document.getElementById('calculator-result');
    const original = document.title;
    document.title = 'MotoHub-Calculation-Result';
    window.print();
    document.title = original;
  };

  return (
    <>
      <Helmet>
        <title>Bike EMI Calculator India | Loan EMI Calculator | MotoHub India</title>
        <meta
          name="description"
          content="Calculate your bike loan EMI in India instantly. Use MotoHub EMI calculator to estimate monthly payments, interest, and total cost."
        />
        <meta
          name="keywords"
          content="bike EMI calculator India, motorcycle loan EMI calculator, EMI bike loan India, bike finance calculator"
        />
        <link rel="canonical" href="https://motohubindia.com/calculator/emi" />
        <meta property="og:title" content="Bike EMI Calculator India | MotoHub India" />
        <meta property="og:description" content="Calculate EMI for your bike loan easily." />
        <meta property="og:url" content="https://motohubindia.com/calculator/emi" />
      </Helmet>

      <div style={{ padding: "30px" }}>
        <CalculatorCard title="Bike EMI Calculator">
          <div style={seoBox}>
            <h2>Bike EMI Calculator India</h2>
            <p style={seoText}>
              Use MotoHub India EMI calculator to estimate your monthly bike loan payments easily. 
              Enter loan amount, interest rate, and tenure to get accurate EMI calculations.
            </p>
            <p style={seoText}>
              This tool helps you plan your budget before buying a bike by showing total interest, 
              monthly EMI, and total repayment amount.
            </p>
          </div>

          <input
            placeholder="Loan Amount"
            value={loan}
            onChange={(e) => setLoan(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="Interest Rate (%)"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="Years"
            value={years}
            onChange={(e) => setYears(e.target.value)}
            style={inputStyle}
          />

          <button style={buttonStyle} onClick={calculateEMI}>
            Calculate EMI
          </button>

          <div id="calculator-result">
            {emiData.length > 0 && (
              <>
                <h3 style={{ marginTop: "20px" }}>EMI Options</h3>
                {emiData.map((item, i) => (
                  <div key={i} style={cardRow}>
                    <div>{item.months} Months</div>
                    <div>₹ {item.emi}</div>
                    <div>Total: ₹ {item.total}</div>
                  </div>
                ))}
                <div className="flex space-x-4">
                  <button onClick={shareOnWhatsApp} 
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm">
                    Share on WhatsApp
                  </button>
                  <button onClick={downloadPDF}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm">
                    Download PDF
                  </button>
                </div>
              </>
            )}
          </div>

          {/* FAQ */}
          <h3 style={{ marginTop: "30px" }}>FAQs</h3>
          <div>
            <p><b>What is EMI?</b></p>
            <p>EMI is the fixed monthly amount you pay for your bike loan.</p>
            <p><b>How is EMI calculated?</b></p>
            <p>EMI depends on loan amount, interest rate, and tenure.</p>
          </div>
        </CalculatorCard>
      </div>
    </>
  );
}

// Styles
const inputStyle = {
  width: "100%",
  padding: "12px",
  marginBottom: "10px",
  borderRadius: "10px",
  border: "1px solid #333",
  background: "#1a1a1a",
  color: "#fff"
};

const buttonStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "none",
  background: "linear-gradient(90deg,#ff4d00,#ff7a00)",
  color: "#fff",
  fontWeight: "bold",
  cursor: "pointer"
};

const cardRow = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px",
  borderBottom: "1px solid #222"
};

const seoBox = {
  marginBottom: "20px",
  background: "#111",
  padding: "15px",
  borderRadius: "10px",
  border: "1px solid #222"
};

const seoText = {
  color: "#aaa",
  lineHeight: "1.6",
  marginTop: "8px"
};